import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import { useAuth } from '../../../../assets/contexts/AuthContext';
import { getSpaceMembers } from '../alli/api/allianceApi';
import AuthorDisplay from '../../../../components/widgets/AuthorDisplay';
import { getAuthorInitial } from '../alli/comm';
import { resolveProfileImageUrl } from '../experts/expertSearchMapping';

function pickerMemberAvatarUrl(member) {
  const au = member?.avatarUrl ?? member?.avatar_url;
  if (au != null && String(au).trim() !== '') return String(au).trim();
  const url = resolveProfileImageUrl(
    member?.profileUid ?? member?.profile_uid,
    member?.profileId ?? member?.profile_id
  );
  return url && String(url).trim() !== '' ? String(url).trim() : '';
}

/**
 * datetime-local 값에서 날짜(YYYY-MM-DD) 추출
 */
const getDatePart = (v) => (v && String(v).split('T')[0]) || '';

/**
 * 참석자 항목 정규화 → { name, org }
 * - 객체 { name, org } 그대로 사용 (org에 "(주)" 등 괄호 포함 가능)
 * - 문자열인 경우: 마지막 " (" 기준으로 이름/기관 분리 (회사명에 "(주)"가 있어도 안전)
 */
const normalizeAttendee = (item) => {
  if (item == null) return { name: '', org: '', userId: '', userUid: null, profileUid: null, profileId: null };
  if (typeof item === 'object' && 'name' in item) {
    return {
      name: String(item.name ?? ''),
      org: String(item.org ?? ''),
      userId: item.userId ?? '',
      userUid: item.userUid ?? item.uid ?? item.id ?? null,
      profileUid: item.profileUid ?? item.profile_uid ?? null,
      profileId: item.profileId ?? item.profile_id ?? null
    };
  }
  const s = String(item).trim();
  const lastBracket = s.lastIndexOf(' (');
  if (lastBracket !== -1) {
    const name = s.slice(0, lastBracket).trim();
    let org = s.slice(lastBracket + 2).trim();
    if (org.endsWith(')')) org = org.slice(0, -1).trim();
    return { name, org, userId: '', userUid: null, profileUid: null, profileId: null };
  }
  return { name: s, org: '', userId: '', userUid: null, profileUid: null, profileId: null };
};

/** 참석자 동일인 판별 — userUid 우선, 없으면 로그인 ID(userId) */
function getAttendeeIdentityKey(entity) {
  if (!entity || typeof entity !== 'object') return '';
  const uid = entity.userUid ?? entity.uid ?? entity.id ?? null;
  if (uid != null && String(uid).trim() !== '') return `uid:${String(uid)}`;
  const loginId = entity.userId != null ? String(entity.userId).trim() : '';
  if (loginId) return `login:${loginId}`;
  return '';
}

function isSameAttendeeIdentity(a, b) {
  const ka = getAttendeeIdentityKey(a);
  const kb = getAttendeeIdentityKey(b);
  if (ka && kb) return ka === kb;
  return (
    String(a?.name ?? '').trim() === String(b?.name ?? '').trim() &&
    String(a?.org ?? '').trim() === String(b?.org ?? '').trim()
  );
}

function formatAttendeeLoginIdDisplay(entity) {
  const loginId = entity?.userId != null ? String(entity.userId).trim() : '';
  return loginId ? `(${loginId})` : '';
}

/**
 * 일정 추가/편집 공통 팝업
 * initialData: { mode, eventId?, title?, start?, end?, description?, attendees? }
 * resolve: { submit, action?, title?, start?, end?, description?, attendees? }
 */
const CalendarEventFormPopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth() || {};
  const initial = config?.initialData ?? {};
  const mode = initial.mode === 'edit' ? 'edit' : 'add';
  const readOnly = Boolean(initial.readOnly);
  const allowSave = initial.allowSave !== false && !readOnly;
  const allowDelete = mode === 'edit' && initial.allowDelete !== false;
  const SCHEDULE_TYPE_OPTIONS = [
    { value: 'MEETING', label: t('popup.calendarEventForm.typeMeeting') },
    { value: 'WORKSHOP', label: t('popup.calendarEventForm.typeWorkshop') },
    { value: 'DEADLINE', label: t('popup.calendarEventForm.typeDeadline') },
    { value: 'EVENT', label: t('popup.calendarEventForm.typeEvent') },
  ];

  const [title, setTitle] = useState(initial.title ?? '');
  const [start, setStart] = useState(initial.start ?? '');
  const [end, setEnd] = useState(initial.end ?? '');
  const [description, setDescription] = useState(initial.description ?? '');
  const [scheduleTpCd, setScheduleTpCd] = useState(initial.scheduleTpCd ?? 'MEETING');
  const [placeNm, setPlaceNm] = useState(initial.placeNm ?? '');
  const [attendees, setAttendees] = useState(() =>
    Array.isArray(initial.attendees) ? initial.attendees.map(normalizeAttendee) : []
  );

  /** 종일 여부 (토글 스위치) */
  const [isAllDay, setIsAllDay] = useState(false);
  /** 참석자 목록(Alliance Space) 선택 드롭다운 표시 여부 */
  const [showAttendeePicker, setShowAttendeePicker] = useState(false);
  const attendeePickerRef = useRef(null);

  const authorForDisplay = useMemo(() => {
    const name = user?.name || user?.userName || user?.userId || '';
    const org = user?.org ?? '';
    const userId = user?.userId ?? user?.id ?? '';
    return { name, org, userId, initial: getAuthorInitial(name) };
  }, [user]);

  useEffect(() => {
    setTitle(initial.title ?? '');
    setStart(initial.start ?? '');
    setEnd(initial.end ?? '');
    setDescription(initial.description ?? '');
    setScheduleTpCd(initial.scheduleTpCd ?? 'MEETING');
    setPlaceNm(initial.placeNm ?? '');
    setAttendees(Array.isArray(initial.attendees) ? initial.attendees.map(normalizeAttendee) : []);
  }, [initial.title, initial.start, initial.end, initial.description, initial.scheduleTpCd, initial.placeNm, initial.attendees]);

  useEffect(() => {
    if (readOnly) setShowAttendeePicker(false);
  }, [readOnly]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!allowSave) return;
    const titleTrim = String(title || '').trim();
    if (!titleTrim) return;
    if (!start) return;

    let finalStart = start;
    let finalEnd = end || start;
    if (isAllDay && start && end) {
      const s = getDatePart(start);
      const e = getDatePart(end);
      finalStart = s ? `${s}T00:00` : start;
      finalEnd = e ? `${e}T23:59` : end;
    }

    onClose(true, {
      action: 'save',
      title: titleTrim,
      start: finalStart,
      end: finalEnd || undefined,
      allDay: isAllDay,
      scheduleTpCd,
      placeNm: placeNm.trim() || undefined,
      description: description.trim() || undefined,
      attendees: attendees.filter((a) => String(a.name || '').trim() || String(a.org || '').trim()).length
        ? attendees.map((a) => ({
            userUid: a.userUid ?? null,
            userId: a.userId ?? '',
            name: a.name,
            org: a.org,
            profileUid: a.profileUid ?? null,
            profileId: a.profileId ?? null
          }))
        : undefined,
    });
  };

  const handleDelete = async () => {
    if (!allowDelete) return;
    // openConfirm 사용 시 팝업이 바뀌어 일정 편집 팝업의 promise가 resolve되지 않으므로, 삭제 확인만 window.confirm 사용
    const ok = window.confirm(t('popup.calendarEventForm.deleteConfirm'));
    if (ok) onClose(true, { action: 'delete' });
  };

  const spaceType = config?.spaceType ?? 'tech-demand';
  const spaceMembers = useMemo(() => {
    if (Array.isArray(config?.spaceMembers)) return config.spaceMembers;
    return getSpaceMembers(spaceType);
  }, [config?.spaceMembers, spaceType]);

  const removeAttendee = (idx) => setAttendees((prev) => prev.filter((_, i) => i !== idx));
  const addAttendeeFromMember = (member) => {
    const next = {
      name: member.name ?? '',
      org: member.org ?? '',
      userId: member.userId ?? '',
      userUid: member.userUid ?? member.uid ?? member.id ?? null,
      profileUid: member.profileUid ?? member.profile_uid ?? null,
      profileId: member.profileId ?? member.profile_id ?? null
    };
    if (attendees.some((a) => isSameAttendeeIdentity(a, next))) return;
    setAttendees((prev) => [...prev, next]);
  };

  useEffect(() => {
    if (!showAttendeePicker) return;
    const handleClickOutside = (e) => {
      if (attendeePickerRef.current && !attendeePickerRef.current.contains(e.target)) setShowAttendeePicker(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAttendeePicker]);

  const popupTitle = mode === 'edit'
    ? t('popup.calendarEventForm.editTitle')
    : t('popup.calendarEventForm.addTitle');

  return (
    <PopupWrapper
      config={{
        ...config,
        title: config?.title ?? popupTitle,
        width: config?.width ?? 600,
        maxHeight: config?.maxHeight ?? '90vh',
        closeOnBackdrop: config?.closeOnBackdrop !== false,
      }}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-calendar-event-form"
    >
      <div className="main-popup-body calendar-event-popup-body">
      <form onSubmit={handleSubmit} className="calendar-event-form">
        <div className="row g-3">
          {/* 제목 */}
          <div className="col-12">
            <label htmlFor="calendarEventTitle" className="form-label">{t('popup.calendarEventForm.titleLabel')}</label>
            <input
              type="text"
              className="form-control"
              id="calendarEventTitle"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </div>

          {/* 작성자 */}
          <div className="col-12">
            <label className="form-label">{t('popup.calendarEventForm.authorLabel')}</label>
            <div className="calendar-event-author" aria-readonly="true">
              <AuthorDisplay
                regUid={user?.uid}
                initial={authorForDisplay.initial}
                name={authorForDisplay.name}
                userId={authorForDisplay.userId ?? ''}
                org={authorForDisplay.org}
              />
            </div>
          </div>

          {/* 일정시작일 | 일정종료일 */}
          <div className="col-12">
            <div className="row g-2 g-md-3">
              <div className="col-12 col-md-6">
                <label htmlFor="calendarEventStart" className="form-label">{t('popup.calendarEventForm.startLabel')}</label>
                <input
                  type={isAllDay ? 'date' : 'datetime-local'}
                  className="form-control"
                  id="calendarEventStart"
                  value={isAllDay ? getDatePart(start) : start}
                  onChange={(e) => (isAllDay ? setStart(e.target.value ? `${e.target.value}T00:00` : '') : setStart(e.target.value))}
                  required
                  readOnly={readOnly}
                  disabled={readOnly}
                />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="calendarEventEnd" className="form-label">{t('popup.calendarEventForm.endLabel')}</label>
                <input
                  type={isAllDay ? 'date' : 'datetime-local'}
                  className="form-control"
                  id="calendarEventEnd"
                  value={isAllDay ? getDatePart(end) : end}
                  onChange={(e) => (isAllDay ? setEnd(e.target.value ? `${e.target.value}T23:59` : '') : setEnd(e.target.value))}
                  readOnly={readOnly}
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>

          {/* 종일(토글) */}
          <div className="col-12 calendar-event-allday-row">
            <label className="form-label">{t('popup.calendarEventForm.allDayLabel')}</label>
            <button
              type="button"
              role="switch"
              aria-checked={isAllDay}
              className={`calendar-event-toggle ${isAllDay ? 'calendar-event-toggle-on' : ''}`}
              onClick={() => setIsAllDay((v) => !v)}
              disabled={readOnly}
            >
              <span className="calendar-event-toggle-thumb" />
            </button>
          </div>

          {/* 참석자 */}
          <div className="col-12 calendar-event-attendee-block" ref={attendeePickerRef}>
          <div className="calendar-event-attendee-label-row">
            <label className="form-label">{t('popup.calendarEventForm.attendeesLabel')} <span className="text-muted fw-normal">({t('popup.calendarEventForm.attendeesMultiHint')})</span></label>
            {!readOnly ? (
            <button
              type="button"
              className={`btn btn-outline-secondary btn-sm calendar-event-attendee-toggle ${showAttendeePicker ? 'is-open' : ''}`}
              onClick={() => setShowAttendeePicker((v) => !v)}
              aria-expanded={showAttendeePicker}
              aria-haspopup="listbox"
              aria-label={showAttendeePicker ? t('popup.calendarEventForm.closeAttendeePicker') : t('popup.calendarEventForm.openAttendeePicker')}
            >
              <span className="calendar-event-attendee-toggle-icon" aria-hidden="true">
                {showAttendeePicker ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">
                    <path d="M3.5 8.5L7 5L10.5 8.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">
                    <path d="M3.5 5.5L7 9L10.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
            </button>
            ) : null}
          </div>
          {showAttendeePicker && !readOnly && (
            <div className="calendar-event-attendee-picker" role="listbox">
              {spaceMembers.length === 0 ? (
                <div className="calendar-event-attendee-picker-empty">{t('popup.calendarEventForm.emptyAttendees')}</div>
              ) : (
                <div className="calendar-event-attendee-picker-grid">
                  {spaceMembers.map((m, memberIdx) => {
                    const selected = attendees.some((a) => isSameAttendeeIdentity(a, m));
                    const loginIdLabel = formatAttendeeLoginIdDisplay(m);
                    const cardKey = getAttendeeIdentityKey(m) || `member-${memberIdx}`;
                    return (
                      <button
                        key={cardKey}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`calendar-event-attendee-picker-card ${selected ? 'is-selected' : ''}`}
                        onClick={() => !selected && addAttendeeFromMember(m)}
                        disabled={selected}
                      >
                        <AuthorDisplay
                          className="calendar-event-picker-author-display"
                          skipUserFetch
                          avatarUrl={pickerMemberAvatarUrl(m) || undefined}
                          initial={getAuthorInitial(m.name)}
                          name={m.name ?? ''}
                          userId={m.userId ?? ''}
                          org={m.org ?? ''}
                          authorCd={m.authorCd ?? ''}
                          showUserId={false}
                        />
                        <span className="calendar-event-attendee-picker-info">
                          <span className="calendar-event-attendee-picker-name">{m.name}</span>
                          {loginIdLabel ? (
                            <span className="calendar-event-attendee-picker-userid">{loginIdLabel}</span>
                          ) : null}
                          {m.org ? (
                            <span className="calendar-event-attendee-picker-org">({m.org})</span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {attendees.length > 0 && (
            <div className="calendar-event-attendee-chips">
              {attendees.map((a, idx) => (
                <span
                  key={`${getAttendeeIdentityKey(a) || a.name}-${idx}`}
                  className="calendar-event-attendee-chip"
                >
                  <AuthorDisplay
                    regUid={a.userUid ?? a.regUid ?? a.uid}
                    avatarUrl={resolveProfileImageUrl(a.profileUid, a.profileId) || undefined}
                    initial={getAuthorInitial(a.name)}
                    name={a.name}
                    userId={a.userId ?? ''}
                    org={a.org ?? ''}
                  />
                  {!readOnly ? (
                  <button type="button" className="calendar-event-attendee-chip-remove" onClick={() => removeAttendee(idx)} aria-label={t('popup.common.remove')}>
                    ×
                  </button>
                  ) : null}
                </span>
              ))}
            </div>
          )}
          </div>

          {/* 설명 */}
          <div className="col-12">
            <label htmlFor="calendarEventDescription" className="form-label">{t('popup.common.description')}</label>
            <textarea
              className="form-control"
              id="calendarEventDescription"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('popup.calendarEventForm.descriptionPlaceholder')}
              readOnly={readOnly}
              disabled={readOnly}
            />
          </div>

          <div className="col-12 calendar-event-type-place-section">
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label htmlFor="calendarEventType" className="form-label">{t('popup.calendarEventForm.typeLabel')}</label>
                <select
                  className="form-select"
                  id="calendarEventType"
                  value={scheduleTpCd}
                  onChange={(e) => setScheduleTpCd(e.target.value)}
                  disabled={readOnly}
                >
                  {SCHEDULE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-6">
                <label htmlFor="calendarEventPlace" className="form-label">{t('popup.calendarEventForm.placeLabel')}</label>
                <input
                  type="text"
                  className="form-control"
                  id="calendarEventPlace"
                  value={placeNm}
                  onChange={(e) => setPlaceNm(e.target.value)}
                  placeholder={t('popup.calendarEventForm.placePlaceholder')}
                  readOnly={readOnly}
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
      </div>

      <div className={`main-popup-footer d-flex ${mode === 'edit' && allowDelete ? 'justify-content-between' : 'justify-content-end'}`}>
        {mode === 'edit' && allowDelete ? (
          <button type="button" className={`btn main-popup-left-btn ${getButtonClass('btn-danger')}`} onClick={handleDelete}>
            {t('popup.common.delete')}
          </button>
        ) : null}
        {allowSave ? (
          <button
            type="button"
            className={`btn main-popup-right-btn ${getButtonClass('btn-primary')}`}
            onClick={handleSubmit}
            disabled={!title.trim() || !start}
          >
            {t('popup.common.save')}
          </button>
        ) : null}
      </div>

      <style>{`
        /* 제목·푸터 고정, 폼 영역만 세로 스크롤 */
        .popup-calendar-event-form.popupContent {
          min-height: 0;
          overflow: hidden;
          align-items: stretch;
        }
        .popup-calendar-event-form.popupContent > .container {
          flex: 1 1 auto;
          min-height: 0;
          width: 100%;
          max-width: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .popup-calendar-event-form .main-popup-title {
          flex-shrink: 0;
        }
        .popup-calendar-event-form .main-popup-body.calendar-event-popup-body {
          flex: 1 1 auto;
          min-height: 0;
          max-height: none;
          overflow-y: auto;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
          padding: 10px 8px 10px 15px;
          box-sizing: border-box;
        }
        .popup-calendar-event-form .main-popup-footer {
          flex-shrink: 0;
        }
        .popup-calendar-event-form .calendar-event-form .form-label { font-weight: 500; color: #374151; }
        .popup-calendar-event-form .form-control { border-radius: 6px; border: 1px solid #e5e7eb; }
        .popup-calendar-event-form .form-control:focus { border-color: #9333ea; box-shadow: 0 0 0 2px rgba(147,51,234,0.15); }
        .popup-calendar-event-form .form-control:disabled { background: #f1f5f9; color: #64748b; }
        .popup-calendar-event-form .calendar-event-type-place-section { margin-bottom: 16px; }
        .popup-calendar-event-form .main-popup-footer { padding-top: 16px; border-top: 1px solid #e5e7eb; }
        .popup-calendar-event-form .calendar-event-allday-row { display: flex; align-items: center; justify-content: space-between; }
        .popup-calendar-event-form .calendar-event-allday-row .form-label { margin-bottom: 0; white-space: nowrap; }
        .popup-calendar-event-form .calendar-event-toggle { width: 44px; height: 24px; border-radius: 12px; border: 1px solid #d1d5db; background: #e5e7eb; padding: 2px; cursor: pointer; transition: background 0.2s, border-color 0.2s; }
        .popup-calendar-event-form .calendar-event-toggle:hover { border-color: #9ca3af; }
        .popup-calendar-event-form .calendar-event-toggle-on { background: var(--theme-color-primary, #9333ea); border-color: var(--theme-color-primary, #9333ea); }
        .popup-calendar-event-form .calendar-event-toggle-thumb { display: block; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.2); transition: transform 0.2s; }
        .popup-calendar-event-form .calendar-event-toggle-on .calendar-event-toggle-thumb { transform: translateX(20px); }
        .popup-calendar-event-form .calendar-event-attendee-block { position: relative; }
        .popup-calendar-event-form .calendar-event-attendee-label-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 8px; }
        .popup-calendar-event-form .calendar-event-attendee-label-row .form-label { margin-bottom: 0; }
        .popup-calendar-event-form .calendar-event-attendee-toggle {
          width: 32px;
          height: 32px;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: #64748b;
          transition: color 0.2s, background 0.2s;
        }
        .popup-calendar-event-form .calendar-event-attendee-toggle:hover { color: #4b5563; background: #f1f5f9; }
        .popup-calendar-event-form .calendar-event-attendee-toggle.is-open { color: #4b5563; background: #f1f5f9; }
        .popup-calendar-event-form .calendar-event-attendee-toggle-icon { display: inline-flex; align-items: center; justify-content: center; }
        .popup-calendar-event-form .calendar-event-attendee-toggle-icon svg { display: block; }
        .popup-calendar-event-form .calendar-event-attendee-picker { height: 220px; overflow-y: auto; margin: 0 0 10px 0; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; position: absolute; left: 0; right: 0; z-index: 10; background: #fff; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
        .popup-calendar-event-form .calendar-event-attendee-picker-empty { padding: 16px; text-align: center; color: #64748b; }
        .popup-calendar-event-form .calendar-event-attendee-picker-grid { display: flex; flex-wrap: wrap; gap: 12px; align-items: stretch; }
        .popup-calendar-event-form .calendar-event-attendee-picker-card { flex: 0 0 auto; max-width: 110px; width: 110px; min-width: 0; display: flex; flex-direction: column; align-items: center; padding: 12px 8px; border: 1px solid #e5e7eb; border-radius: 10px; background: #fff; cursor: pointer; transition: border-color 0.2s, background 0.2s, box-shadow 0.2s; text-align: center; align-self: stretch; }
        .popup-calendar-event-form .calendar-event-attendee-picker-card:hover:not(:disabled) { border-color: var(--theme-color-primary, #9333ea); background: #faf5ff; box-shadow: 0 2px 8px rgba(147,51,234,0.12); }
        .popup-calendar-event-form .calendar-event-attendee-picker-card:disabled { cursor: default; opacity: 0.6; background: #f8fafc; }
        .popup-calendar-event-form .calendar-event-attendee-picker-card .calendar-event-picker-author-display.author-display { margin-bottom: 8px; flex-direction: column; align-items: center; gap: 0; flex-wrap: nowrap; justify-content: flex-start; }
        .popup-calendar-event-form .calendar-event-attendee-picker-card .calendar-event-picker-author-display .alli-author-name,
        .popup-calendar-event-form .calendar-event-attendee-picker-card .calendar-event-picker-author-display .alli-author-org { display: none !important; }
        .popup-calendar-event-form .calendar-event-attendee-picker-card .calendar-event-picker-author-display .alli-author-initial { width: 40px; height: 40px; font-size: 16px; margin: 0; }
        .popup-calendar-event-form .calendar-event-attendee-picker-card .calendar-event-picker-author-display .alli-author-initial:not(.alli-author-initial--has-avatar) { background: var(--theme-color-primary, #9333ea); color: #fff; }
        .popup-calendar-event-form .calendar-event-attendee-picker-card.is-selected .calendar-event-picker-author-display .alli-author-initial:not(.alli-author-initial--has-avatar) { background: #94a3b8; }
        .popup-calendar-event-form .calendar-event-attendee-picker-card.is-selected .calendar-event-picker-author-display .alli-author-initial.alli-author-initial--has-avatar { box-shadow: inset 0 0 0 2px rgba(148, 163, 184, 0.9); }
        .popup-calendar-event-form .calendar-event-attendee-picker-info { flex: 1; display: flex; flex-direction: column; justify-content: center; gap: 2px; min-width: 0; width: 100%; }
        .popup-calendar-event-form .calendar-event-attendee-picker-name { font-size: 13px; font-weight: 500; color: #334155; line-height: 1.35; word-break: break-word; overflow-wrap: break-word; }
        .popup-calendar-event-form .calendar-event-attendee-picker-userid { font-size: 11px; color: #64748b; line-height: 1.35; word-break: break-all; overflow-wrap: anywhere; }
        .popup-calendar-event-form .calendar-event-attendee-picker-org { font-size: 11px; color: #64748b; line-height: 1.35; word-break: break-word; overflow-wrap: break-word; }
        .popup-calendar-event-form .calendar-event-attendee-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
        .popup-calendar-event-form .calendar-event-attendee-chip { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; background: #f1f5f9; border-radius: 999px; font-size: 13px; }
        .popup-calendar-event-form .calendar-event-attendee-chip-remove { padding: 0 2px; border: none; background: none; cursor: pointer; font-size: 16px; line-height: 1; color: #64748b; min-width: 28px; min-height: 28px; }
        .popup-calendar-event-form .calendar-event-attendee-chip-remove:hover { color: #dc2626; }
        @media (max-width: 576px) {
          .popup-calendar-event-form .calendar-event-form { padding: 0 2px; }
          .popup-calendar-event-form .calendar-event-row { flex-direction: column; gap: 10px; align-items: stretch; }
          .popup-calendar-event-form .calendar-event-row-title-author { flex-direction: column; }
          .popup-calendar-event-form .calendar-event-col-2, .popup-calendar-event-form .calendar-event-col-1 { flex: 1 1 100%; min-width: 0; max-width: none; }
          .popup-calendar-event-form .calendar-event-row-datetime { flex-direction: column; }
          .popup-calendar-event-form .calendar-event-datetime-cell { min-width: 0; }
          .popup-calendar-event-form .calendar-event-form .form-control { min-height: 44px; font-size: 16px; }
          .popup-calendar-event-form .calendar-event-attendee-label-row .calendar-event-attendee-toggle { min-width: 44px; min-height: 44px; width: 44px; height: 44px; }
          .popup-calendar-event-form .calendar-event-attendee-picker { height: 200px; padding: 10px; left: -2px; right: -2px; }
          .popup-calendar-event-form .calendar-event-attendee-picker-grid { gap: 10px; }
          .popup-calendar-event-form .calendar-event-attendee-picker-card { max-width: 90px; width: 90px; padding: 10px 6px; }
          .popup-calendar-event-form .calendar-event-attendee-picker-card .calendar-event-picker-author-display { margin-bottom: 6px; }
          .popup-calendar-event-form .calendar-event-attendee-picker-card .calendar-event-picker-author-display .alli-author-initial { width: 36px; height: 36px; font-size: 14px; }
          .popup-calendar-event-form .calendar-event-attendee-picker-name { font-size: 12px; }
          .popup-calendar-event-form .calendar-event-attendee-picker-userid { font-size: 10px; }
          .popup-calendar-event-form .calendar-event-attendee-picker-org { font-size: 10px; }
          .popup-calendar-event-form .main-popup-footer { flex-wrap: wrap; gap: 10px; padding: 12px 0 0; }
          .popup-calendar-event-form .main-popup-footer .btn { min-height: 44px; }
          .popup-calendar-event-form .main-popup-left-btn, .popup-calendar-event-form .main-popup-right-btn { width: 100%; max-width: none; }
        }
      `}</style>
    </PopupWrapper>
  );
};

export default CalendarEventFormPopup;
